import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../../../api-services/ApiClient";
import { SITELINKS } from "../../../app-routes/links";
import Avatar from "../../../app-components/ui/Avatar";
import StatusPill from "../../../app-components/ui/StatusPill";
import { formatDate, getErrorMessage } from "../../../utils";
import DashCard from "./DashCard";
import { fmtMoney, num } from "./format";

/* Uses the existing list endpoints (page 1), so these work with today's API. */

type ListState<T> = { rows: T[]; loading: boolean; error: string };

const useLatest = <T,>(endpoint: string, refreshKey: number): ListState<T> => {
  const [state, setState] = useState<ListState<T>>({ rows: [], loading: true, error: "" });
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    apiClient
      .get(endpoint, { params: { page: 1 } })
      .then((res: any) => alive && setState({ rows: (res?.data?.data ?? []).slice(0, 6), loading: false, error: "" }))
      .catch((err) => alive && setState({ rows: [], loading: false, error: getErrorMessage(err) }));
    return () => {
      alive = false;
    };
  }, [endpoint, refreshKey]);
  return state;
};

const Rows = ({ loading, error, empty, children }: { loading: boolean; error: string; empty: boolean; children: React.ReactNode }) => {
  if (loading)
    return (
      <ul className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i} className="flex items-center gap-3">
            <span className="h-9 w-9 animate-pulse rounded-full bg-hcBg" />
            <span className="h-3 flex-1 animate-pulse rounded bg-hcBg" />
          </li>
        ))}
      </ul>
    );
  if (error) return <p className="py-6 text-center text-sm text-red-600">{error}</p>;
  if (empty) return <p className="py-6 text-center text-sm text-hcMuted">Nothing here yet.</p>;
  return <ul className="divide-y divide-hcBorder">{children}</ul>;
};

const viewAll = (to: string) => (
  <Link to={to} className="text-xs font-semibold text-honeConnectPurple hover:underline">
    View all
  </Link>
);

export const RecentSignups = ({ refreshKey, className }: { refreshKey: number; className?: string }) => {
  const { rows, loading, error } = useLatest<any>("elite-users", refreshKey);
  return (
    <DashCard title="Latest users" action={viewAll(SITELINKS.ELITE_USER)} className={className}>
      <Rows loading={loading} error={error} empty={!rows.length}>
        {rows.map((u) => (
          <li key={u.id}>
            <Link to={`${SITELINKS.USER_PROFILE}${u.id}`} className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-hcBg">
              <Avatar name={u.full_name} src={u.linkedin_photo ?? u.profile_pix_link} size={36} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-honeConnectBlack">{u.full_name ?? u.username}</p>
                <p className="truncate text-xs text-hcMuted">
                  {[u.country?.name, u.created_at ? formatDate(u.created_at, "date") : null].filter(Boolean).join(" · ")}
                </p>
              </div>
              <StatusPill label={u.elite_status ?? u.status ?? "Pending"} />
            </Link>
          </li>
        ))}
      </Rows>
    </DashCard>
  );
};

// Each payment is stored in the currency it was paid in (USD as USD, NGN as NGN),
// so `amount` is shown as-is with its own symbol. Needs `currency` ("NGN" | "USD")
// on the transaction; without it, NGN is assumed.
const paidAmount = (t: any) => {
  const cur = String(t.currency ?? "NGN").toUpperCase() === "USD" ? "USD" : "NGN";
  return fmtMoney(cur, num(t.amount));
};

export const RecentTransactions = ({ refreshKey, className }: { refreshKey: number; className?: string }) => {
  const { rows, loading, error } = useLatest<any>("transactions", refreshKey);
  return (
    <DashCard title="Latest transactions" action={viewAll(SITELINKS.TRANSACTIONS)} className={className}>
      <Rows loading={loading} error={error} empty={!rows.length}>
        {rows.map((t) => {
          const user = t.subscription?.user;
          return (
            <li key={t.id} className="flex items-center gap-3 py-2.5">
              <Avatar name={user?.full_name} src={user?.profile_pix_link} size={36} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-honeConnectBlack">{user?.full_name ?? t.transaction_code}</p>
                <p className="truncate text-xs text-hcMuted">
                  {[t.subscription?.subscription_plan, t.created_at ? formatDate(t.created_at, "date") : null]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-honeConnectBlack">
                  {paidAmount(t)}
                </p>
                <StatusPill label={t.status} />
              </div>
            </li>
          );
        })}
      </Rows>
    </DashCard>
  );
};
